import { PlaybookScriptItemSchema } from "@frontierx/types";
import { z } from "zod";
import { DetectorConfigSchema } from "./DetectorConfig";
import { QuestionSchema } from "./Question";
import { ScriptItemInsertionSchema } from "./ScriptItemInsertion";
import { TranscriptUpdateSchema } from "./TranscriptUpdate";

export const GetHelpPayloadSchema = z.object({
  question: z.string().optional(),
  detectedQuestion: QuestionSchema.optional(),
  transcriptItemIds: z.array(z.string()).optional(),
  orgId: z.string(),
});

export type GetHelpPayload = z.infer<typeof GetHelpPayloadSchema>;

// A record of a single similarity check (e.g., keyword match)
export const ScriptDetectionCheckSchema = z.object({
  method: z.enum([
    "local_keyword",
    "keyword",
    "cohere_paraphrase",
    "openai_embedding",
  ]),
  score: z.number(),
  // We'll record both thresholds for context
  partialThreshold: z.number(),
  fullThreshold: z.number(),
});
export type ScriptDetectionCheck = z.infer<typeof ScriptDetectionCheckSchema>;

// The comprehensive log for a single transcript processing event
export const ScriptDetectionLogPayloadSchema = z.object({
  timestamp: z.number(),
  scriptItemId: z.string(),
  scriptItemText: z.string(),
  triggeringText: z.string(),

  // An array of all checks performed during this event
  checksPerformed: z.array(ScriptDetectionCheckSchema),

  // The final decision based on all the checks
  decision: z.enum([
    "completed",
    "ai_check_recommended",
    "no_match",
    "skipped",
    "ai_completed", // For when the final AI check completes it
  ]),

  // A clear, human-readable summary of why the decision was made
  reason: z.string(),
});
export type ScriptDetectionLogPayload = z.infer<
  typeof ScriptDetectionLogPayloadSchema
>;

export const BaseClientMessageSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("GET_HELP"),
    payload: GetHelpPayloadSchema,
  }),
  z.object({
    type: z.literal("SIMULATE_BUYING_SIGNAL"),
    payload: z.object({}).optional(),
  }),
  z.object({
    type: z.literal("SIMULATE_QUESTION"),
    payload: z.object({}).optional(),
  }),
  z.object({
    type: z.literal("SIMULATE_CONVERSATION"),
    payload: z.object({}).optional(),
  }),
  z.object({
    type: z.literal("SIMULATE_TRANSCRIPT"),
    payload: z.object({
      text: z.string(),
      mode: z.enum(["guest", "host"]),
      time: z.number(),
    }),
  }),
  z.object({
    type: z.literal("SIMULATE_FULL_SCRIPT"),
    payload: z.object({}).optional(),
  }),
  z.object({
    type: z.literal("RESET"),
    payload: z.object({}).optional(),
  }),
  z.object({
    type: z.literal("REQUEST_BOT_INFO"),
    payload: z.object({}).optional(),
  }),
  z.object({
    type: z.literal("REQUEST_SUMMARY"),
    payload: z.object({}).optional(),
  }),
  z.object({
    type: z.literal("SIMULATE_CURRENT_SCRIPT_ITEM"),
    payload: PlaybookScriptItemSchema,
  }),
  z.object({
    type: z.literal("COMPLETE_SCRIPT_ITEM"),
    payload: PlaybookScriptItemSchema,
  }),
  z.object({
    type: z.literal("UNCOMPLETE_SCRIPT_ITEM"),
    payload: PlaybookScriptItemSchema,
  }),
  z.object({
    type: z.literal("SET_CURRENT_SCRIPT_ITEM"),
    payload: PlaybookScriptItemSchema,
  }),
  z.object({
    type: z.literal("PROCESS_SCRIPT"),
    payload: z.boolean(),
  }),
  z.object({
    type: z.literal("TRANSCRIPT_UPDATE"),
    payload: TranscriptUpdateSchema,
  }),
  z.object({
    type: z.literal("INSERT_SCRIPT_ITEM"),
    payload: ScriptItemInsertionSchema,
  }),
  z.object({
    type: z.literal("INITIALIZE_FEEDBACK"),
    payload: z.object({
      needsIntroCard: z.boolean(),
      hostName: z.string(),
      companyName: z.string(),
      timestamp: z.number(),
      customerPersona: z.any().optional(),
    }),
  }),
  z.object({
    type: z.literal("REJOIN_CALL"),
    payload: z.object({
      needsIntroCard: z.boolean(),
      hostName: z.string(),
      companyName: z.string(),
      timestamp: z.number(),
    }),
  }),
  z.object({
    type: z.literal("UPDATE_DETECTOR_CONFIG"),
    payload: DetectorConfigSchema.partial(),
  }),
  z.object({
    type: z.literal("SCRIPT_DETECTION_LOG"),
    payload: ScriptDetectionLogPayloadSchema,
  }),
  z.object({
    type: z.literal("ASSIGN_PARTICIPANT_ROLE"),
    payload: z.object({
      recallParticipantId: z.string(),
      role: z.enum(["salesperson", "client", "observer"]),
      displayName: z.string(),
    }),
  }),
  z.object({
    type: z.literal("GET_PARTICIPANT_ROLES"),
    payload: z.object({}).optional(),
  }),
  z.object({
    type: z.literal("REFRESH_PLAYBOOK"),
    payload: z.object({}).optional(),
  }),
]);

const TimestampSchema = z.object({
  timestamp: z.string().datetime({ precision: 3 }),
});

export const ClientMessageSchema = z.intersection(
  BaseClientMessageSchema,
  TimestampSchema,
);

export type BaseClientMessage = z.infer<typeof BaseClientMessageSchema>;
export type ClientMessage = z.infer<typeof ClientMessageSchema>;

// Type-safe message constructor with validation
export function createClientMessage<T extends BaseClientMessage>(
  message: T,
): ClientMessage {
  // Runtime validation to catch schema mismatches
  const validatedMessage = BaseClientMessageSchema.parse(message);

  return {
    ...validatedMessage,
    timestamp: new Date().toISOString(),
  };
}

// Type-safe message validator (for messages already with timestamp)
export function validateClientMessage(message: unknown): ClientMessage {
  return ClientMessageSchema.parse(message);
}

// Type-safe sendClientEvent factory
export function createTypeSafeSendClientEvent(
  sendFunction: (data: string) => void,
  options?: {
    logMessages?: boolean;
    onError?: (error: Error, message: BaseClientMessage) => void;
  },
) {
  return function sendClientEvent<T extends BaseClientMessage>(
    message: T,
  ): void {
    try {
      // Create and validate message at compile-time and runtime
      const validatedMessage = createClientMessage(message);

      // Optional logging
      if (
        options?.logMessages ||
        message.type === "REQUEST_SUMMARY" ||
        message.type === "REQUEST_BOT_INFO"
      ) {
        console.log("Sending client event:", validatedMessage.type);
      }

      // Send the validated message
      sendFunction(JSON.stringify(validatedMessage));
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error : new Error(String(error));
      console.error("Failed to send client event:", errorMessage);
      options?.onError?.(errorMessage, message);
    }
  };
}

export const ClientMessageTypeSchema = z.enum([
  "UPDATE_DETECTOR_CONFIG",
  "SCRIPT_DETECTION_LOG",
  "OPENAI_SCRIPT_COMPLETION", // New message type for OpenAI queue callbacks
]);
export type ClientMessageType = z.infer<typeof ClientMessageTypeSchema>;

// OpenAI queue callback message payload
export const OpenAIScriptCompletionMessageSchema = z.object({
  type: z.literal("OPENAI_SCRIPT_COMPLETION"),
  payload: z.object({
    scriptItemId: z.string(),
    confidence: z.number().min(0).max(1),
    completed: z.boolean(),
    reasoning: z.string().optional(),
  }),
});
export type OpenAIScriptCompletionMessage = z.infer<
  typeof OpenAIScriptCompletionMessageSchema
>;
